import React, { useState, useEffect, useRef, useMemo } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  FaTimes,
  FaUndo,
  FaExclamationTriangle,
  FaSpinner,
} from 'react-icons/fa';

/**
 * ModalDevolverPrestacao
 * ----------------------------------------------------------------------------
 * Devolve uma prestação ao motorista para correção, com MOTIVO OBRIGATÓRIO.
 * Ao confirmar, chama POST /api/gastos/:id/devolver e dispara o push (backend).
 *
 * O motorista recebe a notificação no app, corrige (valor travado + justificativa
 * obrigatória) e a prestação volta para análise.
 *
 * Props:
 *   gasto     (object) — { id, descricao, valor, ... } a prestação a devolver
 *   onClose   (fn)     — fecha sem devolver
 *   onDevolvida (fn)   — callback após devolução bem-sucedida (para refetch)
 */

const MOTIVO_MIN = 10;
const MOTIVO_MAX = 2000;

const formatCurrency = (value) => {
  const num = parseFloat(value);
  return Number.isNaN(num)
    ? 'R$ 0,00'
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
};

// Sugestões rápidas — clicáveis, apenas prefixam o campo (o gestor completa).
const SUGESTOES = [
  'Valor lançado diferente do comprovante.',
  'Comprovante ilegível ou incompleto.',
  'Comprovante não corresponde à despesa.',
  'Falta anexar o comprovante.',
];

export default function ModalDevolverPrestacao({ gasto, onClose, onDevolvida }) {
  const [motivo, setMotivo]   = useState('');
  const [enviando, setEnviando] = useState(false);
  const textareaRef = useRef(null);

  const motivoLimpo = motivo.trim();
  const valido = motivoLimpo.length >= MOTIVO_MIN;
  const restante = MOTIVO_MAX - motivo.length;

  const descricao = useMemo(() => {
    const raw = gasto?.descricao || '';
    // remove o prefixo [OS-Tarefa #ID] se houver, só para exibição
    return raw.replace(/^\[OS-Tarefa\s+#\d+\]\s*/i, '').trim() || 'Prestação de contas';
  }, [gasto]);

  useEffect(() => {
    // foco no textarea ao abrir
    const t = setTimeout(() => textareaRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !enviando) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, enviando]);

  const aplicarSugestao = (txt) => {
    setMotivo((prev) => {
      const base = prev.trim();
      if (!base) return txt + ' ';
      return `${base} ${txt} `;
    });
    textareaRef.current?.focus();
  };

  const handleConfirmar = async () => {
    if (!valido || enviando) return;
    setEnviando(true);
    try {
      const { data } = await api.post(`/api/gastos/${gasto.id}/devolver`, {
        motivo: motivoLimpo,
      });
      const push = data?.push_enviado || 0;
      toast.success(
        push > 0
          ? 'Prestação devolvida. O motorista foi notificado no app.'
          : 'Prestação devolvida para correção.'
      );
      onDevolvida?.(gasto.id, data);
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.error || 'Não foi possível devolver a prestação.';
      toast.error(msg);
      setEnviando(false);
    }
  };

  return (
    <div className="cgdev-overlay" onMouseDown={enviando ? undefined : onClose}>
      <div
        className="cgdev-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Devolver prestação para correção"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Cabeçalho */}
        <div className="cgdev-head">
          <div className="cgdev-head__title">
            <span className="cgdev-head__icon"><FaUndo /></span>
            <div>
              <h3>Devolver para correção</h3>
              <p>O motorista será notificado e poderá corrigir a prestação.</p>
            </div>
          </div>
          <button
            className="cgdev-close"
            onClick={onClose}
            disabled={enviando}
            aria-label="Fechar"
          >
            <FaTimes />
          </button>
        </div>

        {/* Resumo da prestação */}
        <div className="cgdev-target">
          <div className="cgdev-target__desc" title={gasto?.descricao}>{descricao}</div>
          <div className="cgdev-target__valor">{formatCurrency(gasto?.valor)}</div>
        </div>

        {/* Aviso */}
        <div className="cgdev-note">
          <FaExclamationTriangle />
          <span>
            Explique com clareza o que precisa ser corrigido. Este motivo aparece
            para o motorista no app.
          </span>
        </div>

        {/* Sugestões rápidas */}
        <div className="cgdev-sugestoes">
          {SUGESTOES.map((s) => (
            <button
              key={s}
              type="button"
              className="cgdev-sugestao"
              onClick={() => aplicarSugestao(s)}
              disabled={enviando}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Campo do motivo */}
        <div className="cgdev-field">
          <label htmlFor="cgdev-motivo">Motivo da devolução</label>
          <textarea
            id="cgdev-motivo"
            ref={textareaRef}
            value={motivo}
            maxLength={MOTIVO_MAX}
            rows={4}
            placeholder="Ex.: O valor lançado (R$ 200,00) não corresponde ao comprovante (R$ 150,00). Reenvie com o valor e a nota corretos."
            onChange={(e) => setMotivo(e.target.value)}
            disabled={enviando}
          />
          <div className="cgdev-field__meta">
            <span className={valido ? 'is-ok' : 'is-warn'}>
              {valido
                ? 'Motivo válido.'
                : `Mínimo de ${MOTIVO_MIN} caracteres (${motivoLimpo.length}/${MOTIVO_MIN}).`}
            </span>
            <span className={restante < 100 ? 'is-warn' : ''}>{restante}</span>
          </div>
        </div>

        {/* Rodapé */}
        <div className="cgdev-foot">
          <button
            className="cgdev-btn cgdev-btn--ghost"
            onClick={onClose}
            disabled={enviando}
          >
            Cancelar
          </button>
          <button
            className="cgdev-btn cgdev-btn--danger"
            onClick={handleConfirmar}
            disabled={!valido || enviando}
          >
            {enviando ? (<><FaSpinner className="cgdev-spin" /> Devolvendo…</>) : (<><FaUndo /> Devolver ao motorista</>)}
          </button>
        </div>
      </div>
    </div>
  );
}